/**
 * 45R Quick Add Modal Handler
 * Handles the Quick Add modal UI customization for 45R products.
 *
 * Architecture:
 *   Horizon's native <variant-picker> custom element is kept intact.
 *   It handles all variant logic: option selection, server fetch, morph,
 *   dispatching variant:selected / variant:update events.
 *   Horizon's <product-form-component> listens to these events and
 *   manages button state, variant ID, price, etc.
 *
 *   This file ONLY handles:
 *   - Modal content insertion and CSS class additions for layout
 *   - 45R-specific UI: instant status text, wishlist click handler,
 *     close button, "View full details" link, eager loading
 *   - Status text uses a pre-loaded inventory map for instant updates;
 *     morph later confirms with server-rendered text.
 *
 * Click handling, fetching, caching, and dialog management are all
 * delegated to Horizon's default quick-add.js via the @custom hook:
 *   quick-add.js → updateQuickAddModal() → window.update45RQuickAddModal()
 */

(function() {
  'use strict';

  /**
   * Updates the Quick Add modal with 45R product content
   * Called from quick-add.js when a 45R product is detected
   */
  window.update45RQuickAddModal = function(productGrid, modalContent, quickAddComponent) {
    // Use outerHTML to include the section-45R-product wrapper for CSS selectors
    var html = productGrid.outerHTML;

    // Remove 'product-details' class to bypass Horizon's hide rule
    // (.quick-add-modal__content .product-details *:not(...) { display: none })
    // but keep 'product-details-45R'
    html = html.replace(/\bproduct-details\b(?!-)/g, '');

    // Insert the HTML — <variant-picker> and <product-form-component> are kept
    // as native custom elements and will auto-initialize via connectedCallback
    modalContent.innerHTML = html;

    // Change color swatch images from lazy to eager loading for faster display
    enableEagerLoadingForColorSwatches(modalContent);

    // Ensure pre-selected variant radios from server HTML are checked
    ensurePreSelection(modalContent);

    // Enable GIF circle animation for size buttons in the modal
    initGifAnimation(modalContent);

    // Initialize 45R-specific components
    initVariantEnhancements(modalContent);
    initImageScrollOnColorChange(modalContent);
    initWishlistHandler(modalContent);
    addCloseButton(modalContent);
    addViewMoreDetailsLink(modalContent, quickAddComponent);
  };

  /**
   * Change color swatch images from lazy to eager loading
   */
  function enableEagerLoadingForColorSwatches(modalContent) {
    modalContent.querySelectorAll('.r45-color-swatch__image').forEach(function(img) {
      img.loading = 'eager';
    });
  }

  /**
   * Ensure pre-selected radio buttons from server HTML are actually checked.
   * When content is fetched and injected via innerHTML, the `checked` attribute
   * should be respected by the browser, but we enforce it as a safety net
   * because <variant-picker>'s connectedCallback or other initialization
   * may interfere with the checked state.
   */
  function ensurePreSelection(modalContent) {
    var picker = modalContent.querySelector('.r45-variant-picker');
    if (!picker) return;
    picker.querySelectorAll('input[type="radio"][data-current-checked="true"]').forEach(function(input) {
      if (!input.checked) {
        input.checked = true;
      }
    });
  }

  /**
   * GIF circle animation for size buttons in the Quick Add modal.
   * Uses the inline .circle-gif elements already present in the variant picker HTML.
   *
   * Why not reuse the product page's overlay (in 45R-variant-picker.liquid)?
   *   1. That script is inline in the snippet — not loaded on pages without a
   *      45R variant picker (e.g. collection pages where quick add is used).
   *   2. Even if loaded, its overlay lives on document.body, which sits behind
   *      <dialog>'s top-layer stacking context and would be invisible.
   *
   * This function works within the modal by toggling visibility on the
   * existing .r45-size-button__circle-animation containers and refreshing
   * the <img> src to replay the GIF from frame 1.
   */
  function initGifAnimation(modalContent) {
    var dialogEl = modalContent.closest('dialog');

    // Clean up previous handler (modal is reused across products)
    if (dialogEl && dialogEl._r45GifAbort) {
      dialogEl._r45GifAbort.abort();
    }

    var firstGif = modalContent.querySelector('.circle-gif');
    if (!firstGif) return;
    var gifSrc = (firstGif.getAttribute('data-gif-src') || '').split('?')[0];
    if (!gifSrc) return;

    var gifBlob = null;
    var prevObjectUrl = null;
    var abortCtrl = new AbortController();
    if (dialogEl) dialogEl._r45GifAbort = abortCtrl;

    // Preload GIF as blob for instant replay without network round-trip
    fetch(gifSrc)
      .then(function(r) { return r.blob(); })
      .then(function(b) { gifBlob = b; })
      .catch(function() {});

    function showGif(label) {
      var container = label.querySelector('.r45-size-button__circle-animation');
      var gif = container ? container.querySelector('.circle-gif') : null;
      if (!container || !gif) return;

      container.style.visibility = 'visible';
      gif.style.opacity = '0.55';

      // Refresh src to replay from frame 1
      if (prevObjectUrl) { URL.revokeObjectURL(prevObjectUrl); prevObjectUrl = null; }
      if (gifBlob) {
        prevObjectUrl = URL.createObjectURL(gifBlob);
        gif.src = prevObjectUrl;
      } else {
        gif.src = gifSrc + '?v=' + Date.now();
      }
    }

    function hideAllGifs() {
      modalContent.querySelectorAll('.r45-size-button__circle-animation').forEach(function(el) {
        el.style.visibility = '';  // Reset to CSS default (hidden)
      });
    }

    // Radio change: size click → show GIF, color click → hide GIF
    modalContent.addEventListener('change', function(e) {
      var input = e.target;
      if (!input || input.type !== 'radio') return;
      hideAllGifs();
      if (input.closest('.r45-variant-option--color')) return;
      var label = input.closest('.r45-size-button-label');
      if (!label) return;
      showGif(label);
    }, { signal: abortCtrl.signal });

    // After morph (variant:update), re-show GIF for currently checked size
    if (dialogEl) {
      dialogEl.addEventListener('variant:update', function() {
        setTimeout(function() {
          hideAllGifs();
          var label = modalContent.querySelector('.r45-size-button-label:has(input:checked)');
          if (label) showGif(label);
        }, 15);
      }, { signal: abortCtrl.signal });
    }

    // Initial: show GIF for pre-selected size (if any)
    var preSelected = modalContent.querySelector('.r45-size-button-label:has(input:checked)');
    if (preSelected) showGif(preSelected);
  }

  /**
   * Variant picker enhancements for the Quick Add modal.
   * - Dynamic size filtering: hide sizes without variants for selected color
   * - Selected display: update "Selected: Color | Size [Status]"
   * - Status text: instant "Last One" / "Sold Out" from inventory map
   */
  function initVariantEnhancements(modalContent) {
    var picker = modalContent.querySelector('.r45-variant-picker');
    if (!picker) return;

    var mapEl = picker.querySelector('[data-45r-inventory-map]');
    if (!mapEl) return;
    var map;
    try { map = JSON.parse(mapEl.textContent); } catch (e) { return; }

    function stripPrefix(val) {
      return (val && val.indexOf(' - ') !== -1) ? val.split(' - ').pop() : val;
    }

    function updatePickerModal() {
      var colorInput = picker.querySelector('.r45-variant-option--color input[type="radio"]:checked');
      var sizeInput = picker.querySelector('.r45-variant-option--size input[type="radio"]:checked');
      var colorVal = colorInput ? colorInput.value : null;
      var sizeVal = sizeInput ? sizeInput.value : null;

      var allUnavailable = false;
      if (colorVal) {
        var labels = picker.querySelectorAll('.r45-size-button-label');
        var visibleCount = 0;
        var unavailableCount = 0;

        labels.forEach(function(label) {
          var inp = label.querySelector('input[type="radio"]');
          if (!inp) return;
          var d = map[colorVal + ' / ' + inp.value];
          if (!d) {
            label.classList.add('is-hidden');
            if (inp.checked) { inp.checked = false; sizeVal = null; }
          } else {
            label.classList.remove('is-hidden');
            visibleCount++;
            if (!d.a || (d.m && d.q <= 0)) {
              label.classList.add('is-unavailable');
              unavailableCount++;
            } else {
              label.classList.remove('is-unavailable');
            }
          }
        });

        allUnavailable = visibleCount > 0 && unavailableCount === visibleCount;
      }

      var display = picker.querySelector('[data-selected-variant-display]');
      if (!display) return;

      var colorEl = display.querySelector('[data-selected-color]');
      var sizeEl = display.querySelector('[data-selected-size]');
      var statusEl = display.querySelector('[data-selected-status]');
      var soldOut = display.getAttribute('data-sold-out-text') || '';
      var lastOne = display.getAttribute('data-last-one-text') || '';

      if (colorEl && colorVal) colorEl.textContent = stripPrefix(colorVal);
      if (sizeEl) sizeEl.textContent = sizeVal || '-';

      if ((colorVal && sizeVal) || allUnavailable) {
        display.classList.remove('is-hidden');
      } else {
        display.classList.add('is-hidden');
      }

      if (statusEl) {
        if (allUnavailable) {
          statusEl.textContent = soldOut;
        } else if (colorVal && sizeVal) {
          var data = map[colorVal + ' / ' + sizeVal];
          if (data && (!data.a || (data.m && data.q <= 0))) {
            statusEl.textContent = soldOut;
          } else if (data && data.q === 1 && data.m) {
            statusEl.textContent = lastOne;
          } else {
            statusEl.textContent = '';
          }
        } else {
          statusEl.textContent = '';
        }
      }
    }

    picker.addEventListener('change', updatePickerModal);

    var dialogEl = modalContent.closest('dialog');
    if (dialogEl) {
      dialogEl.addEventListener('variant:update', function() {
        setTimeout(updatePickerModal, 10);
      });
    }

    updatePickerModal();
  }

  /**
   * Scroll the media gallery to the variant's featured image on color change.
   *
   * Each color radio has a data-color-media-id attribute (set by Liquid) with
   * the featured media ID for that color. On color radio change, reads the
   * attribute and scrolls to the matching gallery image.
   */
  function initImageScrollOnColorChange(modalContent) {
    var dialogEl = modalContent.closest('dialog');
    if (!dialogEl) return;

    // Clean up previous handler (modal is reused across products)
    if (dialogEl._r45ScrollAbort) {
      dialogEl._r45ScrollAbort.abort();
    }
    var abortCtrl = new AbortController();
    dialogEl._r45ScrollAbort = abortCtrl;

    // Track current media to avoid redundant scrolls
    var currentMediaId = null;
    var checkedColor = modalContent.querySelector('.r45-variant-option--color input[type="radio"]:checked');
    if (checkedColor) {
      currentMediaId = checkedColor.getAttribute('data-color-media-id');
    }

    function scrollToMedia(mediaId) {
      if (!mediaId || mediaId === currentMediaId) return;
      currentMediaId = mediaId;

      var mediaContainer = modalContent.querySelector('.product-media-45R');
      if (!mediaContainer) return;

      var galleryImage = mediaContainer.querySelector(
        '.r45-gallery__image[data-media-id="' + mediaId + '"]'
      );
      if (!galleryImage) return;

      // Use getBoundingClientRect for reliable positioning regardless of offsetParent chain
      var containerRect = mediaContainer.getBoundingClientRect();
      var imageRect = galleryImage.getBoundingClientRect();

      if (window.innerWidth < 750) {
        // Mobile: horizontal scroll
        mediaContainer.scrollTo({
          left: mediaContainer.scrollLeft + (imageRect.left - containerRect.left),
          behavior: 'smooth'
        });
      } else {
        // Desktop: vertical scroll
        mediaContainer.scrollTo({
          top: mediaContainer.scrollTop + (imageRect.top - containerRect.top),
          behavior: 'smooth'
        });
      }
    }

    // Listen for color radio change — read media ID directly from the radio's
    // data-color-media-id attribute (set by Liquid), avoiding any name-matching issues
    modalContent.addEventListener('change', function(e) {
      var input = e.target;
      if (!input || input.type !== 'radio') return;
      if (!input.closest('.r45-variant-option--color')) return;
      scrollToMedia(input.getAttribute('data-color-media-id'));
    }, { signal: abortCtrl.signal });
  }

  /**
   * Add close button to the modal
   */
  function addCloseButton(modalContent) {
    if (modalContent.querySelector('.r45-close-button')) return;

    var dialog = modalContent.closest('dialog');
    if (!dialog) return;

    var closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'r45-close-button';
    closeButton.setAttribute('aria-label', window.__45R_i18n_close || 'Close');
    closeButton.innerHTML = '<span class="r45-close-icon"></span>';

    closeButton.addEventListener('click', function() {
      var quickAddDialog = dialog.closest('quick-add-dialog');
      if (quickAddDialog && typeof quickAddDialog.closeDialog === 'function') {
        quickAddDialog.closeDialog();
      } else {
        dialog.close();
      }
    });

    modalContent.insertBefore(closeButton, modalContent.firstChild);
  }

  /**
   * Add "View more details" link below the CTA button
   */
  function addViewMoreDetailsLink(modalContent, quickAddComponent) {
    var productForm = modalContent.querySelector('product-form-component[data-product-url]');
    if (!productForm) return;

    var productUrl = productForm.getAttribute('data-product-url');
    if (!productUrl) return;

    var pathname = window.location.pathname;
    var isCollectionPage = pathname.includes('/collections/');
    var isHomePage = pathname === '/' || pathname === '';
    var openInSameWindow = isCollectionPage || isHomePage;

    var link = document.createElement('a');
    link.href = productUrl;
    if (!openInSameWindow) {
      link.target = '_blank';
      link.rel = 'noopener';
    }
    link.className = 'view-more-details-link';
    var viewDetailsText = window.__45R_i18n_viewDetails || 'View full details';
    link.innerHTML = viewDetailsText + ' <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" width="16" height="16" style="display: inline-block; vertical-align: middle; margin-left: 4px;"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" /></svg>';

    var buyButtons = modalContent.querySelector('.product-details-45R__buy-buttons');
    if (buyButtons) {
      buyButtons.appendChild(link);
    }
  }

  /**
   * Initialize wishlist button handler for the modal.
   * The wishlist button is already in the correct position (status row)
   * from the server-rendered Liquid. We just need to wire up the
   * Swym API click handler and variant:update data sync.
   */
  function initWishlistHandler(modalContent) {
    var dialogEl = modalContent.closest('dialog');
    var wishlistButton = modalContent.querySelector('.r45-variant-picker__size-row .wishlist-button');
    if (!wishlistButton) return;

    // Sync wishlist variant data on variant:update (after morph)
    if (dialogEl) {
      dialogEl.addEventListener('variant:update', function(e) {
        var variant = e.detail && e.detail.resource;
        // Re-query after morph (button element may have been replaced)
        var wb = modalContent.querySelector('.r45-variant-picker__size-row .wishlist-button');
        if (wb && variant) {
          wb.setAttribute('data-variant-id', variant.id);
          if (variant.featured_media) {
            wb.setAttribute('data-variant-image', variant.featured_media.preview_image?.src || '');
          }
          wb.setAttribute('data-variant-title', variant.title || '');
          wb.setAttribute('data-variant-price', variant.price || 0);
          wishlistButton = wb;
          checkWishlistState(variant.id);
        }
      });
    }

    function checkWishlistState(variantId) {
      if (!wishlistButton || typeof window._swat === 'undefined' || !window._swat.fetch) return;
      window._swat.fetch(function(products) {
        if (!products) return;
        var existing = products.find(function(p) {
          return String(p.epi) === String(variantId);
        });
        setWishlistButtonState(!!existing);
      });
    }

    function setWishlistButtonState(inWishlist) {
      if (!wishlistButton) return;
      wishlistButton.setAttribute('data-in-wishlist', inWishlist ? 'true' : 'false');
      wishlistButton.setAttribute('aria-label', inWishlist ? (window.__45R_i18n_removeFromWishlist || 'Remove from Wishlist') : (window.__45R_i18n_addToWishlist || 'Add to Wishlist'));
    }

    wishlistButton.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      if (typeof window._swat === 'undefined') return;

      var isInWishlist = wishlistButton.getAttribute('data-in-wishlist') === 'true';
      var productForm = modalContent.querySelector('product-form-component[data-product-url]');
      var productUrl = productForm ? productForm.getAttribute('data-product-url') : '';
      var productId = wishlistButton.getAttribute('data-product-id');
      var titleEl = modalContent.querySelector('.product-details-45R__title');

      var productData = {
        epi: parseInt(wishlistButton.getAttribute('data-variant-id')),
        empi: parseInt(productId),
        du: window.location.origin + productUrl,
        dt: titleEl?.textContent?.trim() || '',
        vi: wishlistButton.getAttribute('data-variant-title') || '',
        iu: wishlistButton.getAttribute('data-variant-image') || '',
        pr: parseFloat(wishlistButton.getAttribute('data-variant-price')) / 100
      };

      if (isInWishlist) {
        setWishlistButtonState(false);
        window._swat.removeFromWishList(productData, function() {
          setWishlistButtonState(false);
        }, function() {
          setWishlistButtonState(true);
        });
      } else {
        setWishlistButtonState(true);
        window._swat.addToWishList(productData, function() {
          setWishlistButtonState(true);
        }, function() {
          setWishlistButtonState(false);
        });
      }
    });
  }

})();